import { Article } from '../types';

export const convertEstimatesArticle: Article = {
  id: 'converting-estimates-invoices',
  title: 'Converting Estimates to Invoices',
  slug: 'converting-estimates',
  description: 'Learn how to efficiently convert approved estimates into invoices, manage the conversion process, and handle various scenarios',
  category: 'finances',
  tags: ['estimates', 'invoices', 'conversion', 'quotes', 'billing', 'approval'],
  lastUpdated: new Date('2024-01-27'),
  readTime: 10,
  content: `
    <h2>Understanding Estimate to Invoice Conversion</h2>
    <p>Converting estimates to invoices is a crucial workflow that helps you quickly bill customers once they approve your quote. This feature saves time by eliminating duplicate data entry and ensures consistency between what was quoted and what is billed.</p>

    <div class="bg-blue-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Key Benefits:</strong>
      <ul>
        <li>Eliminate duplicate data entry</li>
        <li>Maintain pricing consistency</li>
        <li>Track conversion rates</li>
        <li>Speed up billing process</li>
        <li>Reduce errors</li>
      </ul>
    </div>

    <h2 id="conversion-basics">Conversion Basics</h2>
    
    <h3>When to Convert Estimates</h3>
    <p>Convert an estimate to an invoice when:</p>
    <ul>
      <li>✓ Customer has approved the estimate (verbally or written)</li>
      <li>✓ Work has been scheduled or completed</li>
      <li>✓ Deposit is required before starting work</li>
      <li>✓ Converting a quote to a recurring service</li>
      <li>✓ Partial work has been completed (for progress billing)</li>
    </ul>

    <h3>What Gets Transferred</h3>
    <p>During conversion, these elements transfer automatically:</p>
    <ul>
      <li><strong>Customer Information:</strong> Name, address, contact details</li>
      <li><strong>Line Items:</strong> All services, products, and descriptions</li>
      <li><strong>Pricing:</strong> Unit prices, quantities, subtotals</li>
      <li><strong>Discounts:</strong> Percentage or fixed amount discounts</li>
      <li><strong>Tax Settings:</strong> Tax rates and exemptions</li>
      <li><strong>Notes:</strong> Internal and customer-facing notes</li>
      <li><strong>Attachments:</strong> Any files attached to the estimate</li>
    </ul>

    <h3>What Doesn't Transfer</h3>
    <p>These elements require manual update:</p>
    <ul>
      <li>Estimate expiration date → Invoice due date</li>
      <li>Estimate status → Invoice status</li>
      <li>Estimate terms → Invoice payment terms</li>
      <li>Scheduled dates → Service completion dates</li>
    </ul>

    <h2 id="standard-conversion">Standard Conversion Process</h2>
    
    <h3>Step 1: Locate the Estimate</h3>
    <ol>
      <li>Navigate to <strong>Finances</strong> → <strong>Estimates</strong></li>
      <li>Use filters to find the estimate:
        <ul>
          <li>Search by estimate number</li>
          <li>Filter by customer name</li>
          <li>Filter by status (Draft, Sent, Viewed, Approved)</li>
          <li>Sort by date</li>
        </ul>
      </li>
      <li>Click on the estimate to open it</li>
    </ol>

    <h3>Step 2: Review Estimate Details</h3>
    <p>Before converting, verify:</p>
    <ul>
      <li>☐ All line items are correct</li>
      <li>☐ Prices haven't changed since approval</li>
      <li>☐ Customer information is current</li>
      <li>☐ Any special terms are noted</li>
      <li>☐ Estimate is marked as "Approved"</li>
    </ul>

    <h3>Step 3: Initiate Conversion</h3>
    <ol>
      <li>Click the <strong>Actions</strong> menu (three dots)</li>
      <li>Select <strong>Convert to Invoice</strong></li>
      <li>A confirmation dialog appears</li>
      <li>Choose conversion type:
        <ul>
          <li><strong>Full Conversion:</strong> Convert entire estimate</li>
          <li><strong>Partial Conversion:</strong> Select specific line items</li>
        </ul>
      </li>
      <li>Click <strong>Continue</strong></li>
    </ol>

    <h3>Step 4: Configure Invoice Settings</h3>
    <p>In the conversion screen, set:</p>
    <ol>
      <li><strong>Invoice Date:</strong> Today's date or service date</li>
      <li><strong>Due Date:</strong> Based on your payment terms
        <ul>
          <li>Due on receipt</li>
          <li>Net 15 (15 days from invoice date)</li>
          <li>Net 30 (30 days from invoice date)</li>
          <li>Custom date</li>
        </ul>
      </li>
      <li><strong>Invoice Number:</strong> Auto-generated or custom</li>
      <li><strong>Service Date:</strong> When work was/will be completed</li>
    </ol>

    <h3>Step 5: Review and Adjust</h3>
    <p>Make any necessary adjustments:</p>
    <ul>
      <li><strong>Update quantities:</strong> If actual differs from estimate</li>
      <li><strong>Add line items:</strong> For additional work performed</li>
      <li><strong>Remove line items:</strong> For work not completed</li>
      <li><strong>Adjust prices:</strong> Only if customer approved changes</li>
      <li><strong>Add notes:</strong> Document any changes from estimate</li>
    </ul>

    <div class="bg-yellow-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Important:</strong> If making significant changes from the original estimate, document the reason and ensure customer approval to avoid disputes.
    </div>

    <h3>Step 6: Complete Conversion</h3>
    <ol>
      <li>Review the final invoice preview</li>
      <li>Click <strong>Create Invoice</strong></li>
      <li>The system will:
        <ul>
          <li>Create the new invoice</li>
          <li>Link it to the original estimate</li>
          <li>Update estimate status to "Converted"</li>
          <li>Open the new invoice for review</li>
        </ul>
      </li>
    </ol>

    <h2 id="partial-conversion">Partial Conversion</h2>
    
    <h3>When to Use Partial Conversion</h3>
    <p>Use partial conversion for:</p>
    <ul>
      <li>Progress billing on large projects</li>
      <li>Phased work completion</li>
      <li>Deposit invoicing</li>
      <li>When customer approves only part of estimate</li>
      <li>Splitting work across multiple invoices</li>
    </ul>

    <h3>Partial Conversion Process</h3>
    <ol>
      <li>Start standard conversion process</li>
      <li>Select <strong>Partial Conversion</strong></li>
      <li>Check boxes next to line items to include</li>
      <li>For partial quantities:
        <ul>
          <li>Click quantity field</li>
          <li>Enter amount to invoice now</li>
          <li>Remaining quantity stays on estimate</li>
        </ul>
      </li>
      <li>Add note explaining partial billing</li>
      <li>Complete conversion</li>
    </ol>

    <h3>Tracking Partial Conversions</h3>
    <p>The system tracks:</p>
    <ul>
      <li><strong>Original Estimate:</strong> Shows "Partially Converted" status</li>
      <li><strong>Conversion History:</strong> Lists all invoices created</li>
      <li><strong>Remaining Balance:</strong> Un-invoiced amount</li>
      <li><strong>Line Item Status:</strong> 
        <ul>
          <li>✓ Fully invoiced items</li>
          <li>⚬ Partially invoiced items</li>
          <li>○ Not yet invoiced items</li>
        </ul>
      </li>
    </ul>

    <h2 id="bulk-conversion">Bulk Conversion</h2>
    
    <h3>Converting Multiple Estimates</h3>
    <p>For recurring or similar services:</p>
    <ol>
      <li>Go to <strong>Estimates</strong> list view</li>
      <li>Filter for approved estimates</li>
      <li>Select checkboxes for estimates to convert</li>
      <li>Click <strong>Bulk Actions</strong> → <strong>Convert to Invoices</strong></li>
      <li>Set default invoice settings:
        <ul>
          <li>Invoice date (today or custom)</li>
          <li>Due date calculation</li>
          <li>Include all line items</li>
        </ul>
      </li>
      <li>Review summary of conversions</li>
      <li>Click <strong>Convert All</strong></li>
    </ol>

    <h3>Bulk Conversion Rules</h3>
    <ul>
      <li>Each estimate creates separate invoice</li>
      <li>Invoice numbers assigned sequentially</li>
      <li>Same due date calculation applies to all</li>
      <li>Individual invoices can be edited after</li>
      <li>Failed conversions listed separately</li>
    </ul>

    <h2 id="deposit-invoicing">Deposit Invoicing</h2>
    
    <h3>Creating Deposit Invoice from Estimate</h3>
    <ol>
      <li>Open approved estimate</li>
      <li>Click <strong>Convert to Invoice</strong></li>
      <li>Select <strong>Deposit Invoice</strong></li>
      <li>Choose deposit type:
        <ul>
          <li><strong>Percentage:</strong> 25%, 50%, etc.</li>
          <li><strong>Fixed Amount:</strong> Specific dollar amount</li>
        </ul>
      </li>
      <li>Enter deposit amount</li>
      <li>System creates invoice with:
        <ul>
          <li>Single line item: "Deposit for [Estimate #]"</li>
          <li>Link to original estimate</li>
          <li>Deposit amount as total</li>
        </ul>
      </li>
    </ol>

    <h3>Final Invoice After Deposit</h3>
    <p>When creating final invoice:</p>
    <ol>
      <li>Convert estimate normally</li>
      <li>System detects existing deposit</li>
      <li>Automatically adds credit line item</li>
      <li>Shows:
        <ul>
          <li>Total work performed: $X</li>
          <li>Less deposit received: -$Y</li>
          <li>Balance due: $Z</li>
        </ul>
      </li>
    </ol>

    <h2 id="recurring-conversion">Recurring Service Setup</h2>
    
    <h3>Converting Estimate to Recurring Invoice</h3>
    <ol>
      <li>Open approved estimate for recurring service</li>
      <li>Click <strong>Convert to Recurring</strong></li>
      <li>Set frequency:
        <ul>
          <li>Weekly</li>
          <li>Bi-weekly</li>
          <li>Monthly</li>
          <li>Quarterly</li>
          <li>Custom schedule</li>
        </ul>
      </li>
      <li>Set start date</li>
      <li>Configure:
        <ul>
          <li>Auto-send invoices</li>
          <li>Auto-charge saved card</li>
          <li>Payment reminders</li>
        </ul>
      </li>
      <li>Click <strong>Create Recurring Invoice</strong></li>
    </ol>

    <h2 id="change-management">Managing Changes During Conversion</h2>
    
    <h3>Price Adjustments</h3>
    <p>If prices need adjustment:</p>
    <ul>
      <li><strong>Minor adjustments (±10%):</strong>
        <ul>
          <li>Update during conversion</li>
          <li>Add note explaining change</li>
          <li>No additional approval needed</li>
        </ul>
      </li>
      <li><strong>Major adjustments (>10%):</strong>
        <ul>
          <li>Create new estimate</li>
          <li>Get customer approval</li>
          <li>Then convert to invoice</li>
        </ul>
      </li>
    </ul>

    <h3>Adding Extra Work</h3>
    <p>For work beyond original estimate:</p>
    <ol>
      <li>Convert original estimate as-is</li>
      <li>Add new line items to invoice</li>
      <li>Mark clearly as "Additional Work"</li>
      <li>Include description of why needed</li>
      <li>Consider separate invoice if significant</li>
    </ol>

    <h3>Handling Deletions</h3>
    <p>For work not performed:</p>
    <ol>
      <li>During conversion, uncheck items not done</li>
      <li>Or set quantity to 0</li>
      <li>Add note: "Not required" or "Customer declined"</li>
      <li>Keep record for future reference</li>
    </ol>

    <h2 id="status-tracking">Status and Tracking</h2>
    
    <h3>Estimate Status After Conversion</h3>
    <table>
      <thead>
        <tr>
          <th>Conversion Type</th>
          <th>Estimate Status</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Full Conversion</td>
          <td>Converted</td>
          <td>Locked, read-only</td>
        </tr>
        <tr>
          <td>Partial Conversion</td>
          <td>Partially Converted</td>
          <td>Can convert remainder</td>
        </tr>
        <tr>
          <td>Deposit Invoice</td>
          <td>Deposit Received</td>
          <td>Awaiting final invoice</td>
        </tr>
        <tr>
          <td>Recurring Setup</td>
          <td>Converted to Recurring</td>
          <td>Links to subscription</td>
        </tr>
      </tbody>
    </table>

    <h3>Finding Linked Documents</h3>
    <p>To view relationships:</p>
    <ol>
      <li>Open either estimate or invoice</li>
      <li>Look for <strong>Related Documents</strong> section</li>
      <li>Shows:
        <ul>
          <li>Original estimate number</li>
          <li>All invoices created</li>
          <li>Conversion date and user</li>
          <li>Any deposits applied</li>
        </ul>
      </li>
    </ol>

    <h2 id="automation">Automation Options</h2>
    
    <h3>Auto-Conversion Rules</h3>
    <p>Set up automatic conversion:</p>
    <ol>
      <li>Go to <strong>Settings</strong> → <strong>Automation</strong></li>
      <li>Create new rule</li>
      <li>Set trigger:
        <ul>
          <li>When estimate approved</li>
          <li>When appointment completed</li>
          <li>On specific date</li>
          <li>After X days from approval</li>
        </ul>
      </li>
      <li>Set action: Convert to invoice</li>
      <li>Configure:
        <ul>
          <li>Invoice date</li>
          <li>Due date</li>
          <li>Auto-send to customer</li>
        </ul>
      </li>
    </ol>

    <h3>Approval Workflows</h3>
    <p>For controlled conversion:</p>
    <ul>
      <li>Require manager approval for conversions</li>
      <li>Set dollar amount thresholds</li>
      <li>Route to specific approver</li>
      <li>Track approval history</li>
    </ul>

    <h2 id="reports">Conversion Reports</h2>
    
    <h3>Conversion Rate Report</h3>
    <p>Track estimate success:</p>
    <ul>
      <li>Total estimates created</li>
      <li>Number converted to invoices</li>
      <li>Conversion percentage</li>
      <li>Average time to conversion</li>
      <li>Value of converted vs. lost estimates</li>
    </ul>

    <h3>Running Conversion Report</h3>
    <ol>
      <li>Go to <strong>Reports</strong> → <strong>Sales Reports</strong></li>
      <li>Select <strong>Estimate Conversion Report</strong></li>
      <li>Set date range</li>
      <li>Filter by:
        <ul>
          <li>Sales rep</li>
          <li>Service type</li>
          <li>Customer segment</li>
        </ul>
      </li>
      <li>Export as CSV or PDF</li>
    </ol>

    <h2 id="troubleshooting">Common Issues and Solutions</h2>
    
    <h3>Conversion Errors</h3>
    <table>
      <thead>
        <tr>
          <th>Error</th>
          <th>Cause</th>
          <th>Solution</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Cannot convert</td>
          <td>Estimate not approved</td>
          <td>Mark estimate as approved first</td>
        </tr>
        <tr>
          <td>Duplicate invoice</td>
          <td>Already converted</td>
          <td>Check for existing invoice</td>
        </tr>
        <tr>
          <td>Missing customer</td>
          <td>Customer deleted</td>
          <td>Restore customer or recreate</td>
        </tr>
        <tr>
          <td>Invalid items</td>
          <td>Service items deleted</td>
          <td>Update estimate with current items</td>
        </tr>
      </tbody>
    </table>

    <h3>Data Discrepancies</h3>
    <ul>
      <li><strong>Prices don't match:</strong>
        <ul>
          <li>Check for price list updates</li>
          <li>Verify no manual overrides</li>
          <li>Ensure tax calculations correct</li>
        </ul>
      </li>
      <li><strong>Missing information:</strong>
        <ul>
          <li>Update customer record</li>
          <li>Fill in required fields</li>
          <li>Check field mapping settings</li>
        </ul>
      </li>
    </ul>

    <h2 id="best-practices">Best Practices</h2>
    
    <h3>Conversion Workflow</h3>
    <ul>
      <li>✓ Always get written approval before converting</li>
      <li>✓ Convert promptly after work completion</li>
      <li>✓ Review all details before converting</li>
      <li>✓ Document any changes from estimate</li>
      <li>✓ Send invoice immediately after conversion</li>
    </ul>

    <h3>Customer Communication</h3>
    <ul>
      <li>Inform customer when converting estimate</li>
      <li>Explain any differences from original quote</li>
      <li>Provide updated total if changed</li>
      <li>Include estimate number on invoice</li>
      <li>Send thank you with invoice</li>
    </ul>

    <h3>Record Keeping</h3>
    <ul>
      <li>Keep estimate history intact</li>
      <li>Document approval method (email, signature)</li>
      <li>Note why partial conversions done</li>
      <li>Track conversion metrics</li>
      <li>Archive completed estimates</li>
    </ul>

    <div class="bg-green-50 p-4 rounded-lg mt-6">
      <strong>Pro Tip:</strong> Create estimate templates for common services with pre-configured conversion settings. This speeds up both estimate creation and invoice conversion while ensuring consistency.
    </div>

    <h2 id="integration">Integration with Other Features</h2>
    
    <h3>Job Management</h3>
    <p>When converting estimate:</p>
    <ul>
      <li>Automatically create job/work order</li>
      <li>Schedule appointments</li>
      <li>Assign to technicians</li>
      <li>Set job status tracking</li>
    </ul>

    <h3>Inventory Management</h3>
    <p>For product-based estimates:</p>
    <ul>
      <li>Reserve inventory upon approval</li>
      <li>Deduct inventory upon conversion</li>
      <li>Update product costs</li>
      <li>Track material usage</li>
    </ul>

    <h3>Customer Portal</h3>
    <p>Customers can:</p>
    <ul>
      <li>View and approve estimates online</li>
      <li>Track conversion to invoice</li>
      <li>See linked documents</li>
      <li>Make payments on converted invoices</li>
    </ul>
  `,
  tableOfContents: [
    { id: 'conversion-basics', title: 'Conversion Basics' },
    { id: 'standard-conversion', title: 'Standard Conversion Process' },
    { id: 'partial-conversion', title: 'Partial Conversion' },
    { id: 'bulk-conversion', title: 'Bulk Conversion' },
    { id: 'deposit-invoicing', title: 'Deposit Invoicing' },
    { id: 'recurring-conversion', title: 'Recurring Service Setup' },
    { id: 'change-management', title: 'Managing Changes' },
    { id: 'status-tracking', title: 'Status and Tracking' },
    { id: 'automation', title: 'Automation Options' },
    { id: 'reports', title: 'Conversion Reports' },
    { id: 'troubleshooting', title: 'Common Issues' },
    { id: 'best-practices', title: 'Best Practices' },
    { id: 'integration', title: 'Integration Features' }
  ],
  relatedArticles: [
    'invoicing-billing-guide',
    'creating-estimates',
    'payment-processing',
    'recurring-invoices'
  ]
};